import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";

import LoadingButton from "@mui/lab/LoadingButton";

import { CopyToClipboard } from "react-copy-to-clipboard";

import LogoutOutlinedIcon from "../assets/LogoutIcon.svg";
import SearchIcon from "../assets/SearchIcon.svg";
import CloseIcon from "../assets/Close-Icon.svg";
import CopyIcon from "../assets/CopyIcon.svg";
import MenuIcon from "../assets/MenuIcon.svg";
import CreateNewScenario from "../assets/CreateNewScenario.svg";
import BackButton from "../assets/BackButton.svg";
import SaveIcon from "../assets/SaveIcon.svg";
import SubmitIcon from "../assets/SubmitIcon.svg";
import UploadDocument from "../assets/UploadDocument.svg";

import { ObjectType } from "../types/tableTypes";

import {
  contentHeader,
  inputAdornMentStyle,
  label,
  renderMainComponent,
  textFieldStyle,
} from "./common/CustomModalStyle";
import CustomModal from "./common/CustomModal";

import { getCookie, removeAllCookie } from "../utils/Common";

import { RootContext } from "../context/RootContext";
import {
  clickContinue,
  logoutApp,
  patchSaveApplication,
} from "../services/network";
import { Popover, Tooltip, useTheme, useMediaQuery } from "@mui/material";
import TermSheetForm from "./termSheet/TermSheetForm";
import KDMWrapper from "./keyDealMetrics/KDMWrapper";
import { SizingTypeValue } from "../types/enums";
import { clearAllData } from "../utils/v2/common";
import { LoansContext } from "../context/LoansContext";
import UploadDocumentButton from "./common/UploadDocumentButton";
import { ParamType } from "../pages/Loans/v2/LoansPagev2";

export default function Header({
  showEvaluatePageBtn = false,
  showEvaluateNewLoan = false,
  showSearch = false,
  showBorder = false,
  showSubmitLoanBtn = false,
  showSaveApplicationBtn = false,
  showBackToListBtn = false,
  handleSaveApplication,
  setApplicationName,
  applicationName,
  isLoading,
  openSaveApplicationModal,
  setOpenSaveApplicationModal,
  evaluationID,
  searchText,
  setSearchText,
  handleSubmitApplication,
  handleSearch,
  disableSubmitApplication,
  disableSaveApplication,
  applicationError,
  setApplicationError,
  showCreateTermSheet,
  showKDMButton,
  showConvertBtn,
  showSendBtn,
  showMenuIcon,
  handleConvertion,
  showGeneralMenuIcon,
  showUploadDocumentsBtn,
  uploadDocument,
  isUnderWriter,
}: {
  showEvaluatePageBtn?: boolean;
  showEvaluateNewLoan?: boolean;
  showSearch?: boolean;
  showBorder?: boolean;
  showSubmitLoanBtn?: boolean;
  showSaveApplicationBtn?: boolean;
  showBackToListBtn?: boolean;
  showLinkBtn?: boolean;
  showDownloadBtn?: boolean;
  validateForm?: () => boolean;
  handleSaveApplication?: (isTrue: boolean) => void;
  setApplicationName?: (value: string) => void;
  applicationName?: string;
  isLoading?: boolean;
  openSaveApplicationModal?: boolean;
  setOpenSaveApplicationModal?: (value: boolean) => void;
  evaluationID?: number;
  searchText?: string;
  setSearchText?: (str: string) => void;
  handleSubmitApplication?: () => void;
  handleSearch?: (e: React.FormEvent) => void;
  disableSubmitApplication?: boolean;
  disableSaveApplication?: boolean;
  applicationError?: string;
  setApplicationError?: (value: string) => void;
  showCreateTermSheet?: boolean;
  showKDMButton?: boolean;
  showConvertBtn?: boolean;
  showSendBtn?: boolean;
  showMenuIcon?: boolean;
  handleConvertion?: any;
  showGeneralMenuIcon?: boolean;
  showUploadDocumentsBtn?: boolean;
  uploadDocument?: (files: File[]) => void;
  isUnderWriter?: boolean;
}) {
  const {
    setDynamicLoader,
    dynamicConfig,
    setUserLoggedIn,
    setEvaluateLoanForm,
    decodedTokenData,
  } = useContext(RootContext);
  const loansContext = useContext(LoansContext);

  // ── Responsive breakpoints ────────────────────────────────────────────────
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const btnSize = isMobile ? "small" : "medium";
  // ─────────────────────────────────────────────────────────────────────────

  const params = useParams<ParamType>();
  const { loanId } = params;

  const {
    loanDetails,
    propertyDetails,
    quickLoanSizingData,
    detailedLoanSizingData,
    setLoanDetails,
  } = loansContext ?? {};

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);

  const [anchorElSubmitMenu, setAnchorElSubmitMenu] =
    useState<null | HTMLElement>(null);
  const [openEscalateModal, setOpenEscalateModal] = useState<boolean>(false);
  const [escalateComment, setEscalateComment] = useState<string>("");
  const [isEscalating, setIsEscalating] = useState<boolean>(false);
  const [escalationCommentError, setEscalationCommentError] =
    useState<string>("");

  const [openApproveRejectModal, setOpenApproveRejectModal] =
    useState<boolean>(false);
  const [approveRejectComment, setApproveRejectComment] = useState<string>("");
  const [isApprovingRejecting, setIsApprovingRejecting] =
    useState<boolean>(false);
  const [approveRejectCommentError, setApproveRejectCommentError] =
    useState<string>("");

  const refreshToken: string = getCookie("sizer_refresh_token") ?? "{}";

  const navigate = useNavigate();
  const { t } = useTranslation();

  const openList = () => navigate("/list-view");
  const openEvaluateLoan = () => navigate("/evaluate-loan");

  const handleBackToListPage = () => {
    clearAllData(loansContext);
    navigate("/list-view");
  };

  const menuContent: any = [
    {
      label: "createNewScenario",
      action: () => {
        clearAllData(loansContext);
        openEvaluateLoan();
        setAnchorElMenu(null);
      },
      icon: CreateNewScenario,
    },
    {
      label: "backToListPage",
      action: () => {
        clearAllData(loansContext);
        openList();
        setAnchorElMenu(null);
      },
      icon: BackButton,
    },
    {
      label: "duplicateLoan",
      action: () => {
        handleDuplicateLoan();
        setAnchorElMenu(null);
      },
      icon: CopyIcon,
    },
  ];

  const generalMenuItems = [
    {
      label: "saveApplication",
      action: () => {
        handleOpenSaveApplicationModal();
        setAnchorElMenu(null);
      },
      icon: SaveIcon,
      disable: disableSaveApplication,
    },
    {
      label: "submitApplication",
      action: () => {
        handleSubmitApplication?.();
        setAnchorElMenu(null);
      },
      icon: SubmitIcon,
      disable: Boolean(disableSubmitApplication),
    },
    {
      label: "duplicateLoan",
      action: () => {
        handleDuplicateLoan();
        setAnchorElMenu(null);
      },
      icon: CopyIcon,
    },
  ];

  const handleOpenSaveApplicationModal = () => {
    setOpenSaveApplicationModal?.(true);
  };

  const handleLogout = async () => {
    setDynamicLoader(true);
    try {
      const formBody: string[] = [];
      const logoutObject: ObjectType = {
        refresh_token: refreshToken,
        client_id: dynamicConfig?.ViteAppClientId,
      };
      Object.keys(logoutObject).forEach((property) => {
        const encodedKey = encodeURIComponent(property);
        const encodedValue = encodeURIComponent(logoutObject[property]);
        formBody.push(`${encodedKey}=${encodedValue}`);
      });
      const formBodyString = formBody.join("&");
      const headers = { "Content-Type": "application/x-www-form-urlencoded" };
      const url = `${dynamicConfig?.ViteAppRealms}/logout`;

      await logoutApp(url, formBodyString, headers);

      removeAllCookie();
      localStorage.removeItem("client_config");
      setUserLoggedIn(false);
    } catch (err) {
      console.error(err);
    } finally {
      setDynamicLoader(false);
    }
  };

  const escalateLoan = async () => {
    if (!escalateComment) {
      setEscalationCommentError(t("commentRequiredForEscalation"));
      return;
    }
    try {
      setDynamicLoader(true);
      setIsEscalating(true);
      const payload = {
        ...loanDetails,
        escalations: [
          ...(loanDetails?.escalations ?? []),
          {
            escalationComment: escalateComment,
            createdBy:
              decodedTokenData?.email ||
              decodedTokenData?.user_id ||
              "Unknown User",
            createdOn: new Date().toISOString(),
            underwritingStatus: "Pending",
          },
        ],
      };
      await patchSaveApplication(loanId, payload);
      setLoanDetails?.({ ...loanDetails, ...payload });
      setOpenEscalateModal(false);
      setEscalateComment("");
    } catch (err) {
      console.error("Something went wrong while escalating the loan", err);
    } finally {
      setDynamicLoader(false);
      setIsEscalating(false);
    }
  };

  const handleDuplicateLoan = async () => {
    setDynamicLoader(true);
    try {
      const { orgId, productType, propertyType, loanType } =
        loansContext?.loanDetails ?? {};
      const orgFundingType = loanDetails?.fundingType;

      const createPayload: any = {
        orgId: orgId || getCookie("orgId"),
        productType,
        propertyType,
      };
      if (loanType) createPayload.loanType = loanType;

      const { data } = await clickContinue(createPayload);
      sessionStorage.setItem(
        "evaluateLoanForm",
        JSON.stringify({ ...createPayload }),
      );
      setEvaluateLoanForm(createPayload);

      const fallbackPropertyDetails =
        loanDetails?.sizingType === SizingTypeValue.DETAILED_LOAN_SIZING
          ? detailedLoanSizingData?.propertyDetails
          : [
              {
                propertyLocation: quickLoanSizingData?.propertyLocation || {},
                propertyInformation:
                  quickLoanSizingData?.propertyInformation || {},
                propertyEconomics: quickLoanSizingData?.propertyEconomics || {},
              },
            ];

      const tempData: ObjectType = {
        ...loanDetails,
        fundingType: orgFundingType === "undefined" ? "normal" : orgFundingType,
        propertyDetails: propertyDetails?.length
          ? propertyDetails
          : fallbackPropertyDetails,
        evaluationID: data?.data?.data?.evaluationID,
        orgId: data?.data?.data?.orgId,
        applicationName: `${applicationName}-Copy`,
        isApplicationSubmitted: false,
        testTabData: loansContext?.testTabData,
        ratesAsOf: loansContext?.ratesAsOfDate,
        leverageAndPricingData: loansContext?.leverageAndPricingData,
        autoCalculatedLoanResults: loansContext?.autoCalculatedLoanResults,
        autoCalculatedPropResults: loansContext?.autoCalculatedPropResults,
        tcLoanId: loanDetails?.tempLoanId,
        takeOutPartner: null,
        rulesResponse: loansContext?.rulesResponse,
        eligiblePartnerResults: {
          ...loansContext?.eligiblePartnerResults,
          takeoutPartnerStatus: null,
        },
        partnersResults: loansContext?.partnersResults,
      };

      delete tempData.documents;

      const newLoanId = data?.data?.loanId;
      await patchSaveApplication(newLoanId, tempData);

      let redirectUrl = `/loan/${createPayload.productType.replace(/ +/g, "")}`;
      if (createPayload?.loanType) {
        redirectUrl = `${redirectUrl}/${createPayload.loanType.replace(/ +/g, "")}`;
      }
      window.open(`${redirectUrl}/${newLoanId}`, "_blank");
    } catch (err) {
      console.error("Something went wrong while duplicating loan", err);
    } finally {
      setDynamicLoader(false);
    }
  };

  async function approveRejectLoan(action: "approve" | "reject") {
    if (!approveRejectComment) {
      setApproveRejectCommentError(t("commentRequiredForApprovalRejection"));
      return;
    }
    try {
      setDynamicLoader(true);
      setIsApprovingRejecting(true);
      const commentType =
        action === "approve" ? "approvalComment" : "rejectionComment";
      const payload = {
        ...loanDetails,
        escalations: [
          ...(loanDetails?.escalations ?? []),
          {
            updatedBy:
              decodedTokenData?.email ||
              decodedTokenData?.user_id ||
              "Unknown User",
            updatedOn: new Date().toISOString(),
            underwritingStatus: action === "approve" ? "Approved" : "Rejected",
            [commentType]: approveRejectComment,
            data: {
              ...detailedLoanSizingData,
            },
          },
        ],
      };
      await patchSaveApplication(loanId, payload);
      setLoanDetails?.({ ...loanDetails, escalations: payload.escalations });
    } catch (err) {
      console.error(
        `Something went wrong while trying to ${action} the loan`,
        err,
      );
    } finally {
      setDynamicLoader(false);
      setOpenApproveRejectModal(false);
      setApproveRejectComment("");
      setIsApprovingRejecting(false);
    }
  }

  // ── Modals ────────────────────────────────────────────────────────────────

  function renderSaveApplicationModal() {
    return (
      <>
        <Grid container style={renderMainComponent}>
          <Grid item>
            <span className="white-color">{t("saveApplication")}</span>
          </Grid>
          <Grid>
            <IconButton
              onClick={() => setOpenSaveApplicationModal?.(false)}
              data-testid="close-modal-button"
            >
              <img src={CloseIcon} />
            </IconButton>
          </Grid>
        </Grid>

        <div className="p-24">
          <div className="mb-20">
            <span style={contentHeader}>{t("evaluationID")}: </span>
            <span>{evaluationID}</span>
          </div>
          <div className="mb-24">
            <label className="mb-8" style={label}>
              {t("applicationName")}
            </label>
            <sup className="label-required">*</sup>
            <TextField
              size="small"
              fullWidth
              data-testid="text-field-modal"
              value={applicationName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setApplicationName?.(e.target.value);
                if (setApplicationError) {
                  if (!e.target.value) {
                    setApplicationError(t("thisFieldIsRequired"));
                  } else {
                    setApplicationError("");
                  }
                }
              }}
              helperText={!applicationName ? applicationError : ""}
            />
          </div>
          <hr />
          <div className="mt-24 mb-24">
            <label className="mb-8" style={label}>
              {t("shareableLink")}
            </label>
            <TextField
              size="small"
              fullWidth
              style={textFieldStyle}
              value={window.location.href}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CopyToClipboard text={location.href}>
                      <IconButton sx={inputAdornMentStyle}>
                        <img src={CopyIcon} />
                      </IconButton>
                    </CopyToClipboard>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>

        <Divider />
        <Box className="p-12">
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setOpenSaveApplicationModal?.(false)}
            className="mr-8"
            data-testid="cancel-button"
          >
            {t("cancel")}
          </Button>
          <LoadingButton
            data-testid="save-button"
            color="primary"
            variant="contained"
            onClick={() => handleSaveApplication?.(false)}
            loading={isLoading}
          >
            {t("save")}
          </LoadingButton>
        </Box>
      </>
    );
  }

  function renderEscalateModal() {
    return (
      <>
        <Grid container style={renderMainComponent}>
          <Grid item>
            <span className="white-color">{t("escalateToUnderwriter")}</span>
          </Grid>
          <Grid>
            <IconButton
              onClick={() => {
                setOpenEscalateModal(false);
                setEscalateComment("");
                setEscalationCommentError("");
              }}
              data-testid="close-escalate-modal-button"
            >
              <img src={CloseIcon} />
            </IconButton>
          </Grid>
        </Grid>

        <div className="p-24">
          <div>
            <label>
              <b>Evaluation Loan ID: {evaluationID}</b>
            </label>
          </div>
          <br />
          <div style={{ marginBottom: "8px" }}>
            <label style={label}>{t("commentText")}*</label>
          </div>
          <TextField
            multiline
            rows={4}
            fullWidth
            size="small"
            value={escalateComment}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEscalateComment(e.target.value);
              if (e.target.value) setEscalationCommentError("");
            }}
            placeholder={t("addYourCommentsHere")}
            data-testid="escalate-comment-field"
            error={Boolean(escalationCommentError)}
            helperText={escalationCommentError}
          />
        </div>

        <Divider />
        <Box className="p-12">
          <Button
            color="primary"
            variant="outlined"
            className="mr-8"
            disabled={isEscalating}
            data-testid="cancel-escalate-button"
            onClick={() => {
              setOpenEscalateModal(false);
              setEscalateComment("");
              setEscalationCommentError("");
            }}
          >
            {t("cancel")}
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            loading={isEscalating}
            data-testid="escalate-button"
            onClick={escalateLoan}
          >
            {t("escalate")}
          </LoadingButton>
        </Box>
      </>
    );
  }

  function renderApproveRejectModal() {
    return (
      <>
        <Grid container style={renderMainComponent}>
          <Grid item>
            <span className="white-color">{t("approveRejectBtnText")}</span>
          </Grid>
          <Grid>
            <IconButton
              onClick={() => {
                setOpenApproveRejectModal(false);
                setApproveRejectComment("");
                setApproveRejectCommentError("");
              }}
              data-testid="close-approve-reject-modal-button"
            >
              <img src={CloseIcon} />
            </IconButton>
          </Grid>
        </Grid>

        <div className="p-24">
          <div>
            <label>
              <b>Evaluation Loan ID: {evaluationID}</b>
            </label>
          </div>
          <br />
          <div style={{ marginBottom: "8px" }}>
            <label style={label}>{t("commentText")}*</label>
          </div>
          <TextField
            multiline
            rows={4}
            fullWidth
            size="small"
            value={approveRejectComment}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setApproveRejectComment(e.target.value);
              if (e.target.value) setApproveRejectCommentError("");
            }}
            placeholder={t("addYourCommentsHere")}
            data-testid="approve-reject-comment-field"
            error={Boolean(approveRejectCommentError)}
            helperText={approveRejectCommentError}
          />
        </div>

        <Divider />
        <Box
          className="p-12"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              sx={{ color: "white", background: "#2ece89" }}
              variant="contained"
              size={btnSize}
              disabled={isApprovingRejecting}
              data-testid="approve-button"
              onClick={() => approveRejectLoan("approve")}
            >
              {t("approveText")}
            </Button>
            <Button
              sx={{ color: "white", background: "#ef4444" }}
              variant="contained"
              size={btnSize}
              disabled={isApprovingRejecting}
              data-testid="reject-button"
              onClick={() => approveRejectLoan("reject")}
            >
              {t("rejectText")}
            </Button>
          </Box>
          <Button
            color="primary"
            variant="outlined"
            size={btnSize}
            disabled={isApprovingRejecting}
            data-testid="cancel-approve-reject-button"
            onClick={() => {
              setOpenApproveRejectModal(false);
              setApproveRejectComment("");
              setApproveRejectCommentError("");
            }}
          >
            {t("cancel")}
          </Button>
        </Box>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    setIsAuthenticated(Boolean(refreshToken));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid="header"
      style={
        showBorder
          ? {
              borderBottom: "1px solid rgb(159, 170, 169)",
              // ✅ Removed fixed height: "10vh" — now expands with content
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 999,
            }
          : { borderBottom: "none" }
      }
    >
      {/* ✅ Responsive padding via Box wrapper */}
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        alignItems="center"
        // ✅ Wrap=nowrap keeps logo + buttons on one line;
        //    the buttons Grid handles its own internal wrapping
        wrap="nowrap"
      >
        {/* ── Logo column — takes ONLY as much space as the image needs ── */}
        <Grid item xs="auto">
          <Box
            component="img"
            sx={{
              width: { xs: "80px", sm: "120px", md: "10vw" },
              maxWidth: "160px",
              display: "block",
            }}
            className="logo"
            src={dynamicConfig?.ViteAppTcLogo}
            alt="Logo"
          />
        </Grid>

        {/* ── Actions column — takes ALL remaining space ── */}
        <Grid item xs={true}>
          <Grid
            container
            spacing={{ xs: 0.5, sm: 1, md: 2 }}
            justifyContent="flex-end"
            alignItems="center"
            sx={{ flexWrap: "wrap", rowGap: { xs: 0.5, sm: 1, md: 0 } }}
          >
            {showBackToListBtn ? (
              <Grid item>
                <Button
                  size={btnSize}
                  onClick={handleBackToListPage}
                  data-testid="back-button"
                  // ✅ Hide label text on xs, show icon-only via tooltip
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {/* Hide text on very small screens */}
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    {t("backToListPage")}
                  </Box>
                  {/* Show icon only on xs */}
                  <Box
                    component="span"
                    sx={{ display: { xs: "inline", sm: "none" } }}
                  >
                    <img
                      src={BackButton}
                      alt="back"
                      style={{ width: 18, height: 18 }}
                    />
                  </Box>
                </Button>
              </Grid>
            ) : null}

            {showKDMButton ? (
              // ✅ Hide KDM button on xs screens (accessible via menu)
              <Grid item sx={{ display: { xs: "none", sm: "block" } }}>
                <KDMWrapper />
              </Grid>
            ) : null}

            {showCreateTermSheet ? (
              <Grid item sx={{ display: { xs: "none", sm: "block" } }}>
                <TermSheetForm
                  evaluationID={evaluationID}
                  handleSaveApplication={handleSaveApplication}
                  isApplicationSubmitted={Boolean(disableSubmitApplication)}
                />
              </Grid>
            ) : null}

            {showSaveApplicationBtn &&
            !showGeneralMenuIcon &&
            !isUnderWriter ? (
              <Grid item>
                <Button
                  size={btnSize}
                  variant="outlined"
                  onClick={handleOpenSaveApplicationModal}
                  data-testid="save-button"
                  disabled={disableSaveApplication}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("save")}
                </Button>
              </Grid>
            ) : null}

            {isUnderWriter ? (
              <Grid item>
                <Button
                  size={btnSize}
                  variant="contained"
                  onClick={() => setOpenApproveRejectModal(true)}
                  data-testid="approve-reject-button"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("approveRejectBtnText")}
                </Button>
              </Grid>
            ) : null}

            {showConvertBtn ? (
              <Grid item>
                <Tooltip
                  title={
                    Boolean(disableSubmitApplication) &&
                    dynamicConfig?.ViteAppKcRealm === "bplsizer"
                      ? t("contactBD")
                      : ""
                  }
                >
                  <div>
                    <LoadingButton
                      size={btnSize}
                      color="primary"
                      variant="contained"
                      onClick={handleConvertion}
                      loading={isLoading}
                      disabled={disableSaveApplication}
                      data-testid="convert-loan-btn"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {/* ✅ Shorter label on mobile */}
                      <Box
                        component="span"
                        sx={{ display: { xs: "none", md: "inline" } }}
                      >
                        {t("convertToDetailedSizing")}
                      </Box>
                      <Box
                        component="span"
                        sx={{ display: { xs: "inline", md: "none" } }}
                      >
                        {t("convertText")}
                      </Box>
                    </LoadingButton>
                  </div>
                </Tooltip>
              </Grid>
            ) : null}

            {showUploadDocumentsBtn && uploadDocument ? (
              <Grid item sx={{ display: { xs: "none", sm: "block" } }}>
                <UploadDocumentButton
                  uploadDocument={uploadDocument}
                  disabled={Boolean(loansContext?.loanDetails?.tcLoanId)}
                />
              </Grid>
            ) : null}

            {/* ── Submit / Escalate split button ─────────────────────── */}
            {showSendBtn ? (
              <Grid item>
                <Tooltip
                  title={
                    Boolean(disableSubmitApplication) &&
                    dynamicConfig?.ViteAppKcRealm === "bplsizer"
                      ? t("contactBD")
                      : ""
                  }
                >
                  <div>
                    <ButtonGroup
                      variant="contained"
                      color="primary"
                      size={btnSize}
                      disabled={Boolean(loansContext?.loanDetails?.tcLoanId)}
                    >
                      <LoadingButton
                        color="primary"
                        variant="contained"
                        size={btnSize}
                        loading={isLoading}
                        data-testid="submit-loan-btn"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {t("submitOrEscalate")}
                      </LoadingButton>
                      <Button
                        size={btnSize}
                        color="primary"
                        data-testid="submit-dropdown-btn"
                        onClick={(e) => setAnchorElSubmitMenu(e.currentTarget)}
                        sx={{ px: 0.5, minWidth: "32px !important" }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </Button>
                    </ButtonGroup>

                    <Popover
                      sx={{ zIndex: 99999 }}
                      open={Boolean(anchorElSubmitMenu)}
                      anchorEl={anchorElSubmitMenu}
                      onClose={() => setAnchorElSubmitMenu(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      slotProps={{
                        paper: {
                          // ✅ Full width on mobile, fixed on larger screens
                          sx: { minWidth: { xs: "90vw", sm: 220 } },
                        },
                      }}
                    >
                      <div style={popoverWrapperStyle}>
                        <MenuActionItem
                          data-testid="submit-option"
                          onClick={() => {
                            handleSubmitApplication?.();
                            setAnchorElSubmitMenu(null);
                          }}
                        >
                          &ensp;{t("submitText")}
                        </MenuActionItem>
                        <MenuActionItem
                          data-testid="escalate-option"
                          onClick={() => {
                            setOpenEscalateModal(true);
                            setAnchorElSubmitMenu(null);
                          }}
                        >
                          &ensp;{t("escalateToUnderwriter")}
                        </MenuActionItem>
                      </div>
                    </Popover>
                  </div>
                </Tooltip>
              </Grid>
            ) : null}
            {/* ─────────────────────────────────────────────────────── */}

            {showMenuIcon ? (
              <Grid item>
                <IconButton
                  size={btnSize}
                  onClick={(e: any) => setAnchorElMenu(e.currentTarget)}
                >
                  <img src={MenuIcon} />
                </IconButton>
                <Popover
                  sx={{ zIndex: 99999 }}
                  open={Boolean(anchorElMenu)}
                  anchorEl={anchorElMenu}
                  onClose={() => setAnchorElMenu(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: {
                      sx: { width: { xs: "70vw", sm: "40vw", md: "16%" } },
                    },
                  }}
                >
                  <div style={popoverWrapperStyle}>
                    {menuContent.map((item: any) => (
                      <MenuActionItem
                        key={item.label}
                        onClick={item.action}
                      >
                        {item?.icon ? <img src={item.icon} alt="icon" /> : null}
                        &ensp;{t(item.label)}
                      </MenuActionItem>
                    ))}
                    <hr
                      style={{
                        background: "#D5D5D5",
                        border: "1px solid #DBE2FF",
                      }}
                    />
                    <MenuActionItem onClick={handleLogout}>
                      <img src={LogoutOutlinedIcon} alt="logout-icon" />
                      &ensp;{t("logout")}
                    </MenuActionItem>
                  </div>
                </Popover>
              </Grid>
            ) : null}

            {showGeneralMenuIcon ? (
              <Grid item>
                <IconButton
                  size={btnSize}
                  onClick={(e: any) => setAnchorElMenu(e.currentTarget)}
                >
                  <img src={MenuIcon} />
                </IconButton>
                <Popover
                  sx={{ zIndex: 99999 }}
                  open={Boolean(anchorElMenu)}
                  anchorEl={anchorElMenu}
                  onClose={() => setAnchorElMenu(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: {
                      sx: { width: { xs: "70vw", sm: "40vw", md: "16%" } },
                    },
                  }}
                >
                  <div style={popoverWrapperStyle}>
                    {generalMenuItems.map((item: any) => (
                      <MenuActionItem
                        key={item.label}
                        onClick={item.action}
                        disabled={Boolean(item?.disable)}
                      >
                        {item?.icon ? (
                          <img
                            src={item.icon}
                            alt="icon"
                            style={{ width: 12, height: 12 }}
                          />
                        ) : null}
                        &ensp;{t(item.label)}
                      </MenuActionItem>
                    ))}
                    <hr
                      style={{
                        background: "#D5D5D5",
                        border: "1px solid #DBE2FF",
                      }}
                    />
                    <MenuActionItem onClick={handleLogout}>
                      <img src={LogoutOutlinedIcon} alt="logout-icon" />
                      &ensp;{t("logout")}
                    </MenuActionItem>
                  </div>
                </Popover>
              </Grid>
            ) : null}

            {showSubmitLoanBtn ? (
              <Grid item>
                <Tooltip
                  title={
                    Boolean(disableSubmitApplication) &&
                    dynamicConfig?.ViteAppKcRealm === "bplsizer"
                      ? t("contactBD")
                      : ""
                  }
                >
                  <div />
                </Tooltip>
              </Grid>
            ) : null}

            {showSearch ? (
              <Grid item sx={{ width: { xs: "100%", sm: "auto" } }}>
                <form onSubmit={handleSearch}>
                  <OutlinedInput
                    size="small"
                    placeholder="Search"
                    value={searchText}
                    // ✅ Full width on mobile
                    fullWidth={isMobile}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchText ? setSearchText(e.target.value) : null
                    }
                    endAdornment={
                      <InputAdornment position="start">
                        <IconButton
                          type="button"
                          size="small"
                          onClick={handleSearch}
                          data-testid="search-btn"
                        >
                          <img src={SearchIcon} alt="search-icon" />
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </form>
              </Grid>
            ) : null}

            {showEvaluateNewLoan ? (
              <Grid item>
                <Button
                  size={btnSize}
                  variant="contained"
                  onClick={openEvaluateLoan}
                  data-testid="evaluate-button"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {/* ✅ Shorter label on mobile */}
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    {t("evaluateNewLoan")}
                  </Box>
                  <Box
                    component="span"
                    sx={{ display: { xs: "inline", sm: "none" } }}
                  >
                    + {t("newText")}
                  </Box>
                </Button>
              </Grid>
            ) : null}

            {showEvaluatePageBtn ? (
              <Grid item sx={{ display: { xs: "none", sm: "block" } }}>
                <Button
                  size={btnSize}
                  variant="outlined"
                  onClick={openList}
                  data-testid="evaluations-page-button"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("evaluationsPage")}
                </Button>
              </Grid>
            ) : null}

            {isAuthenticated && !(showMenuIcon || showGeneralMenuIcon) ? (
              <Grid item>
                <Button
                  size={btnSize}
                  variant="outlined"
                  onClick={handleLogout}
                  data-testid="logout-button"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {/* ✅ Icon only on mobile */}
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    {t("logout")}&ensp;
                  </Box>
                  <img src={LogoutOutlinedIcon} alt="logout-icon" />
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>

      {/* Modals */}
      {openSaveApplicationModal ? (
        <CustomModal
          open={openSaveApplicationModal}
          handleClose={setOpenSaveApplicationModal}
          children={renderSaveApplicationModal()}
          className={"saveApplication"}
        />
      ) : null}

      {openEscalateModal ? (
        <CustomModal
          open={openEscalateModal}
          handleClose={() => setOpenEscalateModal(false)}
          children={renderEscalateModal()}
          className={"escalateModal"}
        />
      ) : null}

      {openApproveRejectModal ? (
        <CustomModal
          open={openApproveRejectModal}
          handleClose={() => setOpenApproveRejectModal(false)}
          children={renderApproveRejectModal()}
          className={"approveRejectModal"}
        />
      ) : null}
    </header>
  );
}

// ── Shared style helpers ──────────────────────────────────────────────────────
const menuItemStyle: React.CSSProperties = {
  color: "#362866",
  textAlign: "left",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "500",
  lineHeight: "normal",
  display: "flex",
  padding: "8px",
  cursor: "pointer",
  alignItems: "center",
};

const popoverWrapperStyle: React.CSSProperties = {
  padding: "8px",
  borderRadius: "8px",
  background: "#FFF",
  boxShadow:
    "0px 2px 12px 0px rgba(0, 0, 0, 0.12), 0px 4px 4px 0px rgba(0, 0, 0, 0.10)",
};

// ── Accessible menu-item wrapper ──────────────────────────────────────────────
// Replaces bare <div onClick> elements in popovers.
// • role="menuitem"  → screen readers identify it as an interactive menu entry
// • tabIndex         → keyboard users can Tab to it (disabled items are skipped)
// • onKeyDown        → Enter / Space trigger the action (WCAG 2.1 §2.1.1)
interface MenuActionItemProps {
  onClick: () => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  children: React.ReactNode;
  "data-testid"?: string;
}

function MenuActionItem({
  onClick,
  style,
  disabled = false,
  children,
  "data-testid": testId,
}: MenuActionItemProps) {
  return (
    <div
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      style={{
        ...menuItemStyle,
        ...style,
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.7 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      data-testid={testId}
    >
      {children}
    </div>
  );
}
